// AIMS PWA - install prompt (Offline Inspection mode).
//
// Gated by permission (Ben, 2026-07-07): only users who can actually inspect see
// the "Install" prompt - office/admin users never do, since the offline value
// prop (and the camera-QR-won't-open-a-PWA limitation) only applies to field
// inspectors. Gate mirrors the backend checkInspectionAuth + the existing
// VisitDetailModal canInspect logic: canUseDetailedInspection || canInspectAsset.
//
// Android/Chrome: intercepts `beforeinstallprompt`, suppresses the browser's auto
// mini-infobar for everyone, and only surfaces a custom "Install" CTA when the
// gate passes. iOS Safari fires no such event, so we show a gated instructional
// hint (Share -> Add to Home Screen) instead. Dismissals are remembered so we
// never nag. Rendered inside the logged-in shell (AppShell).

import React, { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

const DISMISS_KEY = 'pwaInstallDismissedAt';
const DISMISS_DAYS = 14; // suppress the prompt for this long after a dismissal

const can = (flag) => localStorage.getItem(flag) === '1';
// Same gate as VisitDetailModal.js / backend checkInspectionAuth.
const canInspect = () => can('canUseDetailedInspection') || can('canInspectAsset');

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const recentlyDismissed = () => {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const when = Number(raw);
  if (!when) return false;
  return Date.now() - when < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null); // Android beforeinstallprompt event
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  const eligible = useCallback(
    () => canInspect() && !isStandalone() && !recentlyDismissed(),
    []
  );

  useEffect(() => {
    // Android/Chrome: capture the event, block the default infobar, gate the CTA.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      if (!eligible()) return; // captured (so no auto-infobar) but never shown
      setDeferred(e);
      setVisible(true);
    };
    // If installed during this session, hide immediately.
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS has no beforeinstallprompt - show the instructional variant if eligible.
    if (isIOS() && eligible()) {
      setIosHint(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [eligible]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (_) {
      /* user dismissed the native sheet - treat as no-op */
    }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Paper
      elevation={6}
      role="dialog"
      aria-label="Install AIMS"
      sx={{
        position: 'fixed',
        left: { xs: 12, sm: 'auto' },
        right: { xs: 12, sm: 24 },
        bottom: { xs: 96, sm: 24 }, // clear the mobile bottom nav (~84px)
        zIndex: (t) => t.zIndex.snackbar,
        maxWidth: 420,
        p: 1.75,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        borderRadius: 'var(--r, 12px)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        color: 'var(--text)',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 38,
          height: 38,
          borderRadius: '10px',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--primary-tint)',
          color: 'var(--primary)',
        }}
      >
        {iosHint ? <IosShareIcon fontSize="small" /> : <InstallMobileIcon fontSize="small" />}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ fontWeight: 700, fontSize: 14, mb: 0.25 }}>Install AIMS</Box>
        {iosHint ? (
          <Box sx={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.4 }}>
            Tap the <IosShareIcon sx={{ fontSize: 15, verticalAlign: 'text-bottom' }} /> Share
            icon, then <b>Add to Home Screen</b> to inspect offline on site.
          </Box>
        ) : (
          <Box sx={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.4 }}>
            Add AIMS to your home screen to take visits offline and inspect with no signal on site.
          </Box>
        )}

        {/* Set the scan expectation up front: a phone's camera app opens QR links in
            the browser, NOT the installed app - so scanning must happen inside AIMS. */}
        <Box sx={{ mt: 1, display: 'flex', gap: 0.75, alignItems: 'flex-start', fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.35 }}>
          <QrCodeScannerIcon sx={{ fontSize: 15, mt: '1px', flexShrink: 0, color: 'var(--primary)' }} />
          <span>
            <b>Scanning tip:</b> your phone's camera app opens QR tags in the browser, not this app.
            To use assets offline, <b>open AIMS first</b>, then scan from inside it.
          </span>
        </Box>

        {!iosHint && (
          <Box sx={{ mt: 1.25, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={install}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Install
            </Button>
            <Button
              size="small"
              onClick={dismiss}
              sx={{ textTransform: 'none', color: 'var(--muted)' }}
            >
              Not now
            </Button>
          </Box>
        )}
      </Box>

      <IconButton size="small" onClick={dismiss} aria-label="Dismiss" sx={{ color: 'var(--muted)', mt: -0.5, mr: -0.5 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}
